import { FC, ReactNode, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Segmented } from 'antd';
import { ListFilter } from 'lucide-react';
import { TbArrowLeft, TbArrowRight, TbSquareRoundedCheckFilled } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { customFilterService } from '@/services';
import OverflowFadeScrollArea from '@/shared/components/layouts/OverflowFadeScrollArea';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { CustomFilterVisibility } from '@/types';

import { buildSearchFilterEnvelope } from '../searchFilters';

export interface SearchFilterChangeHandler {
    (path: string[], value: unknown): void;
}

export interface SearchFiltersSiderProps {
    activeFilterCount: number;
    filters: unknown;
    filtersView: ReactNode;
    onSaveSuccess?: () => void | Promise<void>;
    savedFiltersView: ReactNode;
}

type SearchFiltersSiderView = 'filters' | 'saved';

const isSearchFiltersSiderView = (value: string | number): value is SearchFiltersSiderView =>
    value === 'filters' || value === 'saved';

const CustomFiltersIcon = () => (
    <svg
        aria-hidden
        fill='none'
        height='0.8rem'
        stroke='currentColor'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        style={{ display: 'block', flex: 'none' }}
        viewBox='0 0 24 24'
        width='0.8rem'
    >
        <path d='M12 5H2' />
        <path d='M6 12h12' />
        <path d='M9 19h6' />
        <path d='M16 5h6' />
        <path d='M19 8V2' />
    </svg>
);

const SegmentedIconLabel: FC<{ icon: ReactNode; label: string }> = ({ icon, label }) => (
    <span
        style={{
            alignItems: 'center',
            display: 'inline-flex',
            gap: '0.25rem',
            justifyContent: 'center',
            lineHeight: 1,
            transform: 'translateY(-1px)',
            verticalAlign: 'middle',
        }}
    >
        <span
            aria-hidden
            style={{
                alignItems: 'center',
                display: 'inline-flex',
                flex: '0 0 0.8rem',
                height: '0.8rem',
                justifyContent: 'center',
                lineHeight: 0,
                transform: 'translateY(-1px)',
                width: '0.8rem',
            }}
        >
            {icon}
        </span>
        <span>{label}</span>
    </span>
);

const SearchFiltersSider: FC<SearchFiltersSiderProps> = ({
    activeFilterCount,
    filters,
    filtersView,
    onSaveSuccess,
    savedFiltersView,
}) => {
    const { token } = useAntDesignTheme();
    const [saving, setSaving] = useState(false);
    const [view, setView] = useState<SearchFiltersSiderView>('filters');

    const saveFilters = async () => {
        try {
            setSaving(true);
            const customFilter = await customFilterService.create({
                query: buildSearchFilterEnvelope(filters),
                title: `Custom Filter (${new Date().toISOString()})`,
                visibility: CustomFilterVisibility.PRIVATE,
            });

            await onSaveSuccess?.();
            toast('Custom Filter Saved', {
                description: customFilter.title,
                icon: <TbSquareRoundedCheckFilled size='1rem' />,
            });
        } catch (error) {
            captureException(error, { tags: { action: 'save-search-filter' } });
            toast.error('Unable to save this filter.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            aria-label='Search filters'
            style={{
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100% + 0.5rem)',
                marginTop: '-0.5rem',
                minHeight: 0,
                position: 'relative',
                width: '100%',
            }}
        >
            <div
                aria-label='Filter views'
                role='toolbar'
                style={{
                    backgroundColor: token.colorBgContainer,
                    borderBottom: `1px solid ${token.colorBorder}`,
                    flex: 'none',
                    padding: '0.25rem 0.75rem',
                }}
            >
                <Segmented
                    block
                    onChange={(value) => {
                        if (isSearchFiltersSiderView(value)) {
                            setView(value);
                        }
                    }}
                    options={[
                        {
                            label: (
                                <SegmentedIconLabel
                                    icon={<ListFilter size='0.8rem' style={{ display: 'block', flex: 'none' }} />}
                                    label='Default Filters'
                                />
                            ),
                            value: 'filters',
                        },
                        {
                            label: <SegmentedIconLabel icon={<CustomFiltersIcon />} label='Custom Filters' />,
                            value: 'saved',
                        },
                    ]}
                    size='small'
                    value={view}
                />
            </div>
            <OverflowFadeScrollArea
                containerStyle={{ boxSizing: 'border-box', flex: '1 1 0%', minHeight: 0 }}
                contentStyle={{ padding: '0.5rem 0.75rem 0' }}
                surface='container'
            >
                {view === 'filters' ? filtersView : savedFiltersView}
            </OverflowFadeScrollArea>
            <div
                style={{
                    borderTop: `1px solid ${token.colorBorder}`,
                    flex: 'none',
                    padding: '0.75rem',
                }}
            >
                {view === 'filters' ? (
                    <Button
                        block
                        disabled={activeFilterCount === 0}
                        loading={saving}
                        onClick={() => void saveFilters()}
                        size='small'
                        type='primary'
                    >
                        Save as Custom Filter
                        <TbArrowRight size='1rem' style={{ verticalAlign: '-0.2rem' }} />
                    </Button>
                ) : (
                    <Button block loading={saving} onClick={() => setView('filters')} size='small' type='dashed'>
                        <TbArrowLeft size='1rem' style={{ verticalAlign: '-0.2rem' }} />
                        Back to Filters
                    </Button>
                )}
            </div>
            <div
                aria-hidden
                data-testid='search-filter-sider-divider'
                style={{
                    backgroundColor: token.colorBorder,
                    bottom: 0,
                    pointerEvents: 'none',
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    width: '1px',
                    zIndex: 2,
                }}
            />
        </div>
    );
};

export default SearchFiltersSider;
